'use client';

import React, { useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { ALGERIAN_WILAYAS } from '@/lib/data/wilayas';
import { OFFICIAL_COMMUNES } from '@/lib/data/official-locations';
import { Button } from '@/components/ecoBuilder/ui/button';
import { Input } from '@/components/ecoBuilder/ui/input';
import { Label } from '@/components/ecoBuilder/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ecoBuilder/ui/select';
import { Loader2, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { createOrder } from '@/actions/order-actions';
import { toast } from 'sonner';
import { RichTextRenderer } from '@/components/ecoBuilder/atoms/RichTextRenderer';
import { createClient } from "@/utils/supabase/client";

const stripHtml = (html: string) => {
    return html.replace(/<[^>]*>?/gm, '');
};

// Schema
const phoneRegex = /^(05|06|07)[0-9]{8}$/;

const checkoutSchema = z.object({
    fullName: z.string().min(2, 'الاسم مطلوب'),
    phone: z.string().regex(phoneRegex, 'رقم الهاتف غير صحيح (يجب أن يبدأ بـ 05, 06, 07 ويتكون من 10 أرقام)'),
    wilaya: z.string().min(1, 'يرجى اختيار الولاية'),
    commune: z.string().min(1, 'البلدية مطلوبة'), // Text input for now
    address: z.string().optional().or(z.literal('')),
    quantity: z.number().min(1).default(1),
});

type CheckoutFormValues = z.infer<typeof checkoutSchema>;

export interface SmartCheckoutProps {
    productId?: string; // NEW: Product ID
    enableName?: boolean;
    enableAddress?: boolean;
    enableQuantity?: boolean;
    buttonText?: string;
    buttonColor?: string;
    successMessage?: string;
    redirectUrl?: string;
    productTitle?: string;
    productPrice?: number;
    currency?: string;
    shippingCost?: number;
    // New Layout Props
    // Layout Props
    layoutMode?: 'vertical' | 'split';
    sideImage?: string;
    imagePosition?: 'right' | 'left';
    alignment?: 'center' | 'right' | 'left';
    // Style Props
    className?: string;
    style?: React.CSSProperties;
}

import { usePageContext } from '@/components/ecoBuilder/renderer/PageContext';
import { useSiteContext } from '@/app/tenant/TenantLayoutWrapper';

export const SmartCheckout: React.FC<SmartCheckoutProps> = ({
    productId,
    enableName = true,
    enableAddress = true,
    enableQuantity = true,
    buttonText = 'أطلب الآن - الدفع عند الاستلام',
    buttonColor,
    successMessage = 'تم استلام طلبك بنجاح! سنتصل بك قريباً للتأكيد.',
    productTitle = 'المنتج',
    productPrice = 0,
    currency: currencyProp = 'DZD',
    shippingCost: defaultShipping = 0,
    layoutMode = 'vertical',
    sideImage,
    imagePosition = 'right',
    alignment = 'center',
    className,
    style
}) => {
    const { themeSettings, siteId } = usePageContext(); // Helper context to get site info
    // const { siteSlug } = useSiteContext(); // Unused
    // const router = useRouter(); // Unused
    const primaryColor = themeSettings?.primaryColor || '#fbbf24';
    const finalButtonColor = buttonColor || primaryColor;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false); // Local success state (if not redirecting)
    const [dynamicShipping, setDynamicShipping] = useState(defaultShipping);

    // NEW: Product State
    const [product, setProduct] = useState<any>(null);


    // NEW: Fetch Logic
    useEffect(() => {
        let isMounted = true;
        const loadProduct = async () => {
            if (!productId && !siteId) return;


            try {
                const supabase = createClient();
                const { data } = await supabase
                    .from('products')
                    .select('*')
                    .eq('id', productId)
                    .single();

                if (isMounted && data) {
                    setProduct(data);
                }
            } catch (err) {
                console.error("Error loading product:", err);
            } finally {

            }
        };

        if (productId) loadProduct();

        return () => { isMounted = false; };
    }, [productId, siteId]);

    // DERIVED VALUES
    const finalTitle = product?.name || productTitle || 'المنتج';
    const finalPrice = product?.price || productPrice || 0;
    const finalImage = product?.images?.[0] || product?.image_url || '';
    const currency = product?.currency || currencyProp;

    const form = useForm<CheckoutFormValues>({
        resolver: zodResolver(checkoutSchema) as any,
        defaultValues: {
            quantity: 1,
            commune: '',
            address: '',
        } as any
    });

    // Update shipping when wilaya changes
    const handleWilayaChange = (code: string) => {
        form.setValue('wilaya', code);
        form.setValue('commune', ''); // Reset commune
        const wilaya = ALGERIAN_WILAYAS.find(w => w.code === code);
        if (wilaya && wilaya.shippingCost) {
            setDynamicShipping(wilaya.shippingCost);
        }
    };

    const onSubmit: SubmitHandler<CheckoutFormValues> = async (data) => {
        if (!siteId) {
            toast.error("خطأ: تعذر تحديد معرف المتجر. يرجى تحديث الصفحة.");
            return;
        }

        setIsSubmitting(true);
        try {
            // Prepare Order Payload
            const orderPayload = {
                customerName: data.fullName,
                customerPhone: data.phone,
                customerWilaya: data.wilaya,
                customerAddress: data.address || '',
                customerCommune: data.commune,
                items: [{
                    id: productId || 'manual-smart-checkout', // TODO: If products are real, link ID
                    name: stripHtml(finalTitle),
                    price: finalPrice,
                    quantity: data.quantity,
                    image: finalImage, // Can be added if needed
                }],
                subtotal: finalPrice * data.quantity,
                shippingCost: dynamicShipping,
                total: (finalPrice * data.quantity) + dynamicShipping,
                paymentMethod: 'cod' as const,
                siteId: siteId
            };

            const result = await createOrder(orderPayload);

            if (result.success) {
                toast.success(`تم استلام طلبك بنجاح! رقم الطلب: ${result.orderNumber}`);
                setIsSuccess(true);
                form.reset();
            } else {
                toast.error(result.error || "حدث خطأ أثناء الطلب");
            }

        } catch (error) {
            console.error(error);
            toast.error("حدث خطأ غير متوقع. حاول مرة أخرى.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const totalPrice = (finalPrice * form.watch('quantity')) + dynamicShipping;

    if (isSuccess) {
        return (
            <div className="p-8 bg-green-50 border border-green-200 rounded-2xl text-center animate-in fade-in zoom-in duration-300">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-green-800 mb-2">شكراً لك!</h3>
                <div className="text-green-700"><RichTextRenderer html={successMessage} /></div>
            </div>
        );
    }

    // Layout Classes
    const alignmentClasses = {
        'center': 'justify-center',
        'right': 'justify-start',
        'left': 'justify-end'
    };

    const containerMaxWidth = layoutMode === 'split' ? 'max-w-5xl' : 'max-w-md';

    return (
        <section
            className={cn("w-full px-4 md:px-0", className)}
            style={style}
            dir="rtl"
        >
            <div className={cn(
                "flex w-full",
                alignmentClasses[alignment] || 'justify-center'
            )}>
                <div
                    className={cn(
                        "w-full bg-card border border-border rounded-2xl shadow-xl overflow-hidden transition-all duration-300",
                        containerMaxWidth
                    )}
                >
                    <div className={cn(
                        "flex flex-col",
                        layoutMode === 'split' && "md:flex-row"
                    )}>

                        {/* Side Image (Only in split mode) */}
                        {layoutMode === 'split' && (sideImage || finalImage) && (
                            <div className={cn(
                                "relative w-full md:w-1/2 min-h-[300px] md:min-h-full bg-muted",
                                imagePosition === 'left' ? "md:order-last" : "md:order-first" // In RTL: First is Right visually. Last is Left visually.
                            )}>
                                <img
                                    src={sideImage || finalImage}
                                    alt={stripHtml(finalTitle || '')}
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            </div>
                        )}

                        {/* Main Form Content */}
                        <div className={cn(
                            "flex-1",
                            layoutMode === 'split' ? "w-full md:w-1/2" : "w-full"
                        )}>
                            <div className="bg-primary/10 p-4 border-b border-border text-center">
                                <h3 className="font-bold text-lg">معلومات التوصيل</h3>
                                <p className="text-xs text-muted-foreground">أدخل معلوماتك للتوصل بالمنتج</p>
                            </div>

                            <form onSubmit={form.handleSubmit(onSubmit)} className="p-6 space-y-4">

                                {/* Name */}
                                {enableName && (
                                    <div className="space-y-2">
                                        <Label>الاسم الكامل</Label>
                                        <Input
                                            {...form.register('fullName')}
                                            placeholder="أدخل اسمك الكامل"
                                            className={cn("h-11", form.formState.errors.fullName && "border-red-500")}
                                        />
                                        {form.formState.errors.fullName && (
                                            <span className="text-xs text-red-500">{form.formState.errors.fullName.message}</span>
                                        )}
                                    </div>
                                )}

                                {/* Phone */}
                                <div className="space-y-2">
                                    <Label>رقم الهاتف</Label>
                                    <div className="relative" dir="ltr">
                                        <span className="absolute left-3 top-2.5 text-sm text-muted-foreground font-semibold tracking-wider z-10 select-nonepointer-events-none">+213</span>
                                        <Input
                                            {...form.register('phone')}
                                            placeholder="5 XX XX XX XX"
                                            type="tel"
                                            className={cn("h-11 pl-14 text-left", form.formState.errors.phone && "border-red-500")}
                                        />
                                    </div>
                                    {form.formState.errors.phone && (
                                        <span className="text-xs text-red-500">{form.formState.errors.phone.message}</span>
                                    )}
                                </div>

                                {/* Location Grid */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="space-y-2">
                                        <Label>الولاية</Label>
                                        <Select onValueChange={handleWilayaChange}>
                                            <SelectTrigger dir="rtl" className={cn("h-11 w-full text-right", form.formState.errors.wilaya && "border-red-500")}>
                                                <SelectValue placeholder="اختر الولاية" />
                                            </SelectTrigger>
                                            <SelectContent dir="rtl">
                                                {ALGERIAN_WILAYAS.map((w) => (
                                                    <SelectItem key={w.code} value={w.code} className="text-right flex-row-reverse">
                                                        {w.code} - {w.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {form.formState.errors.wilaya && (
                                            <span className="text-xs text-red-500">{form.formState.errors.wilaya.message}</span>
                                        )}
                                    </div>

                                    <div className="space-y-2">
                                        <Label>البلدية</Label>
                                        <Select onValueChange={(val) => form.setValue('commune', val)}>
                                            <SelectTrigger
                                                dir="rtl"
                                                className={cn("h-11 w-full text-right", form.formState.errors.commune && "border-red-500")}
                                                disabled={!form.watch('wilaya')}
                                            >
                                                <SelectValue placeholder={form.watch('wilaya') ? "اختر البلدية" : "اختر الولاية أولاً"} />
                                            </SelectTrigger>
                                            <SelectContent dir="rtl">
                                                {form.watch('wilaya') && OFFICIAL_COMMUNES[parseInt(form.watch('wilaya')).toString()]?.map((commune) => (
                                                    <SelectItem key={commune} value={commune} className="text-right flex-row-reverse">
                                                        {commune}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {form.formState.errors.commune && (
                                            <span className="text-xs text-red-500">{form.formState.errors.commune.message}</span>
                                        )}
                                    </div>
                                </div>

                                {/* Address */}
                                {enableAddress && (
                                    <div className="space-y-2">
                                        <Label>العنوان (اختياري)</Label>
                                        <Input {...form.register('address')} placeholder="الحي، رقم المنزل..." className="h-11" />
                                    </div>
                                )}

                                {/* Quantity */}
                                {enableQuantity && (
                                    <div className="space-y-2">
                                        <div className="flex justify-between">
                                            <Label>الكمية</Label>
                                            <span className="text-xs font-bold text-primary">{totalPrice.toLocaleString()} {currency}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="icon"
                                                onClick={() => {
                                                    const current = form.getValues('quantity');
                                                    if (current > 1) form.setValue('quantity', current - 1);
                                                }}
                                            >
                                                -
                                            </Button>
                                            <Input
                                                {...form.register('quantity', { valueAsNumber: true })}
                                                type="number"
                                                className="text-center font-bold"
                                                min={1}
                                            />
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="icon"
                                                onClick={() => {
                                                    const current = form.getValues('quantity');
                                                    form.setValue('quantity', current + 1);
                                                }}
                                            >
                                                +
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {/* Summary */}
                                <div className="bg-muted p-3 rounded-lg text-sm space-y-1">
                                    <div className="flex justify-between">
                                        <span>المنتج:</span>
                                        <span><RichTextRenderer html={finalTitle} inline /></span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>السعر:</span>
                                        <span>{finalPrice.toLocaleString()} {currency}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span>التوصيل:</span>
                                        <span>{dynamicShipping === 0 ? 'مجاني' : `${dynamicShipping} ${currency}`}</span>
                                    </div>
                                    <div className="border-t border-border pt-2 mt-2 flex justify-between font-bold text-base">
                                        <span>الإجمالي للدفـع:</span>
                                        <span className="text-primary">{totalPrice.toLocaleString()} {currency}</span>
                                    </div>
                                </div>

                                {/* Submit */}
                                <Button
                                    type="submit"
                                    className="w-full h-12 text-lg font-bold shadow-lg transition-transform hover:scale-[1.02] active:scale-[0.98]"
                                    style={{ backgroundColor: finalButtonColor, color: '#000' }}
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                            جاري الطلب...
                                        </>
                                    ) : (
                                        <RichTextRenderer html={buttonText} inline />
                                    )}
                                </Button>

                                <p className="text-center text-xs text-muted-foreground mt-2 flex items-center justify-center gap-1">
                                    <CheckCircle className="w-3 h-3 text-green-500" />
                                    دفع عند الاستلام - آمن ومضمون
                                </p>

                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

// EDITOR CONFIGURATION

export const SmartCheckoutDefaultProps: Partial<SmartCheckoutProps> = {
    enableName: true,
    enableAddress: true,
    enableQuantity: true,
    buttonText: 'أطلب الآن - الدفع عند الاستلام',
    buttonColor: '#fbbf24',
    successMessage: 'تم استلام طلبك بنجاح! سنتصل بك قريباً للتأكيد.',
    productTitle: 'المنتج',
    productPrice: 0,
    currency: 'DZD',
    shippingCost: 500,
    layoutMode: 'vertical',
    alignment: 'center'
};

export const SmartCheckoutSchema: any[] = [
    {
        key: 'productId',
        label: 'اختر المنتج',
        type: 'product',
        description: 'سيتم جلب تفاصيل المنتج (الاسم، السعر، الصورة) تلقائياً عند اختياره.'
    },
    {
        key: 'layoutMode',
        label: 'تخطيط النموذج (Layout)',
        type: 'select',
        options: [
            { value: 'vertical', label: 'عمودي (مكدس)' },
            { value: 'split', label: 'أفقي (مع صورة)' },
        ]
    },
    {
        key: 'sideImage',
        label: 'صورة جانبية (فقط للوضع الأفقي)',
        type: 'image', // Assuming 'image' type exists in EditorFieldType, otherwise 'text' or 'url'
    },
    {
        key: 'imagePosition',
        label: 'موقع الصورة',
        type: 'select',
        options: [
            { value: 'right', label: 'يمين' },
            { value: 'left', label: 'يسار' },
        ]
    },
    {
        key: 'alignment',
        label: 'محاذاة المكون بالكامل',
        type: 'select',
        options: [
            { value: 'right', label: 'يمين الصفحة' },
            { value: 'center', label: 'وسط الصفحة' },
            { value: 'left', label: 'يسار الصفحة' },
        ]
    },
    {
        key: 'productTitle',
        label: 'اسم المنتج',
        type: 'text',
        placeholder: 'اسم المنتج الذي سيظهر في الملخص'
    },
    {
        key: 'productPrice',
        label: 'سعر المنتج',
        type: 'number',
        defaultValue: 0
    },
    {
        key: 'shippingCost',
        label: 'تكلفة الشحن الافتراضية',
        type: 'number',
        defaultValue: 500
    },
    {
        key: 'buttonText',
        label: 'نص الزر',
        type: 'text',
        defaultValue: 'أطلب الآن'
    },
    {
        key: 'buttonColor',
        label: 'لون الزر',
        type: 'color',
        defaultValue: '#fbbf24'
    },
    {
        key: 'successMessage',
        label: 'رسالة النجاح',
        type: 'textarea',
        defaultValue: 'تم استلام طلبك بنجاح!'
    },
    {
        key: 'enableName',
        label: 'تفعيل حقل الاسم',
        type: 'boolean',
        defaultValue: true
    },
    {
        key: 'enableAddress',
        label: 'تفعيل حقل العنوان',
        type: 'boolean',
        defaultValue: true
    },
    {
        key: 'enableQuantity',
        label: 'تفعيل اختيار الكمية',
        type: 'boolean',
        defaultValue: true
    }
];
